/* ============================================================
   SmartTagSetup.jsx – Full Production Version (Strict Mode)
   - Owner vs Finder mode
   - Strict profile updates (no guessing)
   - Health reminders & records
   - Pet notes logging via chat
   - OpenAI SmartTag fallback
   ============================================================ */


import React, { useState, useEffect, useRef, useLayoutEffect } from "react";

import { Spinner, Form, Badge, Button } from "react-bootstrap";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Auth, Storage } from "aws-amplify";
import "./SmartTagSetup.css";
import "../../styles/ChatTheme.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaw,
  faPaperPlane,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";

import { getSubscriptionStatus } from "../../lib/subscription";
import FloatingPawButton from "../../components/FloatingPawButton";
import PetPageHeader from "../../components/PetPageHeader";

const OPENAI_LAMBDA_URL =
  "https://tiwqg7ei5lnwvl2o3oudz26z7q0vzzwc.lambda-url.us-east-1.on.aws/";
const SAVE_PET_LAMBDA_URL =
  "https://pax3jhmxsuub7f7fdnm3xa3gta0bbtok.lambda-url.us-east-1.on.aws/";
const PET_HEALTH_LAMBDA_URL =
  "https://rpjj6asb422e7dke7qx4hkouf40shzpk.lambda-url.us-east-1.on.aws/";
const PET_NOTES_LAMBDA_URL =
  "https://mycobvpzx7pwy35eru5utxdatu0ioamb.lambda-url.us-east-1.on.aws/";

const STRICT_MODE = true;

export default function SmartTagSetup({ finderMode = false, subInfo }) {
  // 🆕 Normalize subscription info
  const status = subInfo?.status || "none";
  const planName = subInfo?.planName || null;

  const rawMax =
    typeof subInfo?.maxTags === "number"
      ? subInfo.maxTags
      : Number(subInfo?.petLimit ?? 0);

  const maxTags = Number.isNaN(rawMax) ? 0 : rawMax;
  const usedTags = Number.isFinite(subInfo?.usedTags)
    ? subInfo.usedTags
    : Number(subInfo?.usedTags ?? 0);

  const atLimit = maxTags > 0 && usedTags > maxTags;

  const [searchParams] = useSearchParams();
  const { tagId: tagParam } = useParams();
  const navigate = useNavigate();
  const queryTag = searchParams.get("tag");
  const isFinderRoute = finderMode || !!queryTag;

  const isFinderIntent = searchParams.get("finder") === "1";

  const rawResolved = (
    queryTag ||
    tagParam ||
    localStorage.getItem("lpp.tagId") ||
    ""
  ).trim();
  const resolvedTagId = rawResolved.toUpperCase();

  // Ownership state
  const [userTagId, setUserTagId] = useState("");
  const [ownershipChecked, setOwnershipChecked] = useState(false);

  // UI state
  const [mode, setMode] = useState("loading"); // "finder" | "owner"
  const [conversation, setConversation] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Subscription state
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subChecked, setSubChecked] = useState(false);



  const [userName, setUserName] = useState("");

  // Onboarding / pet state
  const [currentStep, setCurrentStep] = useState(null);
  const [pet, setPet] = useState({
    tagId: resolvedTagId || "",
    name: "",
    species: "",
    breed: "",
    age: "",
  });

  // STRICT update mode (for existing profile)
  const [profileUpdateMode, setProfileUpdateMode] = useState(false);
  const [updateField, setUpdateField] = useState(null); // 'name' | 'species' | 'breed' | 'age'

  // Health flow state
  const [healthFlow, setHealthFlow] = useState(null);

  // Pet photo state (read-only display)
  const [petPhotoUrl, setPetPhotoUrl] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  // right near the top of the component:
const chatEndRef = useRef(null);
// ✅ Voice (Polly)
const [voiceEnabled, setVoiceEnabled] = useState(false); // user choice
const voicePrimedRef = useRef(false);

const audioRef = useRef(null);
const audioUrlRef = useRef(null);

// queue: { b64, vol }
const audioQueueRef = useRef([]);
const isPlayingRef = useRef(false);

// keep latest conversation for async speaking
const convoRef = useRef([]);
useEffect(() => {
  convoRef.current = conversation;
}, [conversation]);

const stopVoice = () => {
  try {
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    voicePrimedRef.current = false;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  } catch {}
};
  const stripEmojis = (text = "") =>
  String(text)
    .replace(
      /[\p{Extended_Pictographic}\p{Emoji_Presentation}\p{Emoji}\u200d]/gu,
      ""
    )
    .replace(/\s+/g, " ")
    .trim();

const playNextFromQueue = () => {
  if (isPlayingRef.current) return;
  const item = audioQueueRef.current.shift();
  if (!item?.b64) return;

  const nextBase64 = item.b64;
  const vol = typeof item.vol === "number" ? item.vol : 1;

  isPlayingRef.current = true;

  try {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }

    const bytes = Uint8Array.from(atob(nextBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: "audio/mpeg" });
    const url = URL.createObjectURL(blob);
    audioUrlRef.current = url;

    if (!audioRef.current) audioRef.current = new Audio();
    audioRef.current.src = url;
    audioRef.current.volume = vol;

    audioRef.current.onended = () => {
      try {
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
          audioUrlRef.current = null;
        }
      } catch {}
      isPlayingRef.current = false;
      playNextFromQueue();
    };

    audioRef.current.play().catch(() => {
      isPlayingRef.current = false;
      playNextFromQueue();
    });
  } catch {
    isPlayingRef.current = false;
    playNextFromQueue();
  }
};

const playPollyBase64 = (audioBase64, vol = 1) => {
  if (!audioBase64) return;
  audioQueueRef.current.push({ b64: audioBase64, vol });
  playNextFromQueue();
};

const fetchPollyForText = async (text, force = false) => {
  if (!force && !voiceEnabled) return null;

  const cleanText = stripEmojis(text || "");
  if (!cleanText) return null;

  try {
    const res = await fetch(OPENAI_LAMBDA_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mode: "tts",
        userMessage: cleanText,
        voice: true,
      }),
    });

    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    return data.audioBase64 || null;
  } catch {
    return null;
  }
};

const primeVoice = async (force = false) => {
  if (!force && !voiceEnabled) return;
  if (voicePrimedRef.current) return;

  try {
    const b64 = await fetchPollyForText("Voice enabled.", true); // ✅ force
    if (!b64) return;

    playPollyBase64(b64, 0); // silent prime
    voicePrimedRef.current = true;
  } catch {}
};


const lastSpokenAiIndexRef = useRef(-1);

const speakLatestAiMessage = async () => {
  if (!voiceEnabled) return;
  if (!voicePrimedRef.current) return;

  const list = convoRef.current || [];
  if (!list.length) return;

  const lastAiIndex = [...list].map((m) => m.sender).lastIndexOf("ai");
  if (lastAiIndex === -1) return;
  if (lastAiIndex <= lastSpokenAiIndexRef.current) return;

  lastSpokenAiIndexRef.current = lastAiIndex;

  const b64 = await fetchPollyForText(list[lastAiIndex].text);
  if (b64) playPollyBase64(b64, 1);
};

useEffect(() => {
  // speak after new AI messages render
  speakLatestAiMessage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [conversation.length]);
useEffect(() => {
  if (!voiceEnabled) return;
  if (!voicePrimedRef.current) return;

  const list = convoRef.current || [];
  if (!list.length) return;

  const lastAiIndex = [...list].map((m) => m.sender).lastIndexOf("ai");
  if (lastAiIndex === -1) return;

  if (lastAiIndex <= lastSpokenAiIndexRef.current) return;
  lastSpokenAiIndexRef.current = lastAiIndex;

  (async () => {
    const b64 = await fetchPollyForText(list[lastAiIndex].text);
    if (b64) playPollyBase64(b64, 1);
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [conversation.length, voiceEnabled]);


useEffect(() => {
  if (chatEndRef.current) {
    // This will scroll either the inner chat container OR the whole page,
    // depending on your CSS — so it’s resilient.
    chatEndRef.current.scrollIntoView({
      behavior: "auto", // or "smooth" if you want
      block: "end",
    });
  }
}, [conversation.length, loading]);


  const addMsg = (sender, text, extras = {}) =>
    setConversation((prev) => [...prev, { sender, text, ...extras }]);

  const addCtaBubble = (text, buttonLabel, route) =>
    setConversation((prev) => [
      ...prev,
      {
        sender: "ai",
        type: "cta",
        text,
        buttonLabel,
        route,
      },
    ]);

  // Convert "one" → "1", "two years" → "2 years"
  function normalizeAgeInput(text) {
    if (!text) return text;
    let t = text.trim();
    if (!t) return t;

    const numberWords = {
      zero: "0",
      one: "1",
      two: "2",
      three: "3",
      four: "4",
      five: "5",
      six: "6",
      seven: "7",
      eight: "8",
      nine: "9",
      ten: "10",
      eleven: "11",
      twelve: "12",
    };

    const parts = t.split(/\s+/);
    if (!parts.length) return t;

    const firstLower = parts[0].toLowerCase();
    if (numberWords[firstLower]) {
      parts[0] = numberWords[firstLower];
      return parts.join(" ");
    }

    return t;
  }

  function parseDateInput(text) {
    if (!text) return null;
    const t = text.trim();

    // MM/DD/YYYY or MM-DD-YYYY
    let m = t.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (m) {
      let [, mm, dd, yyyy] = m;
      mm = mm.padStart(2, "0");
      dd = dd.padStart(2, "0");
      return {
        canonical: `${yyyy}-${mm}-${dd}`,
        display: `${mm}/${dd}/${yyyy}`,
      };
    }

    // YYYY-MM-DD
    m = t.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
    if (m) {
      let [, yyyy, mm, dd] = m;
      mm = mm.padStart(2, "0");
      dd = dd.padStart(2, "0");
      return {
        canonical: `${yyyy}-${mm}-${dd}`,
        display: `${mm}/${dd}/${yyyy}`,
      };
    }

    // MM/DD/YY → assume 20xx
    m = t.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2})$/);
    if (m) {
      let [, mm, dd, yy] = m;
      mm = mm.padStart(2, "0");
      dd = dd.padStart(2, "0");
      const yyyy = Number(yy) < 50 ? `20${yy}` : `19${yy}`;
      return {
        canonical: `${yyyy}-${mm}-${dd}`,
        display: `${mm}/${dd}/${yyyy}`,
      };
    }

    return null;
  }

  function parseTimeInput(text) {
    if (!text) return null;
    const lower = text.trim().toLowerCase();

    // "3pm", "3 pm", "3:30pm", "3:30 pm", "15:30"
    const m = lower.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);

    if (!m) return null;

    let [, hStr, mStr, ap] = m;
    let hours = Number(hStr);
    let minutes = mStr ? Number(mStr) : 0;

    if (Number.isNaN(hours) || hours < 0 || hours > 23) return null;
    if (Number.isNaN(minutes) || minutes < 0 || minutes > 59) return null;

    if (ap) {
      if (hours < 1 || hours > 12) return null;
      if (ap === "pm" && hours < 12) hours += 12;
      if (ap === "am" && hours === 12) hours = 0;
    }

    const hh = String(hours).padStart(2, "0");
    const mm = String(minutes).padStart(2, "0");

    let displayHour = hours % 12;
    if (displayHour === 0) displayHour = 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    const display =
      minutes === 0
        ? `${displayHour}${ampm}`
        : `${displayHour}:${mm}${ampm}`;

    return {
      canonical: `${hh}:${mm}`,
      display,
    };
  }

  function formatDateTimeDisplay(raw) {
    if (!raw) return "";

    let parsed = new Date(raw);

    if (Number.isNaN(parsed.getTime())) {
      const match = raw
        .trim()
        .match(
          /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm|AM|PM)?)?$/
        );

      if (match) {
        const [, y, m, d, hRaw, minRaw, apRaw] = match;
        const year = Number(y);
        const monthIndex = Number(m) - 1;
        const day = Number(d);

        let hours = hRaw ? Number(hRaw) : 0;
        let minutes = minRaw ? Number(minRaw) : 0;

        if (apRaw) {
          const ap = apRaw.toLowerCase();
          if (ap === "pm" && hours < 12) hours += 12;
          if (ap === "am" && hours === 12) hours = 0;
        }

        parsed = new Date(year, monthIndex, day, hours, minutes);
      }
    }

    if (Number.isNaN(parsed.getTime())) {
      return raw;
    }

    const month = parsed.getMonth() + 1;
    const day = parsed.getDate();
    const year = parsed.getFullYear();

    let hours = parsed.getHours();
    const minutes = parsed.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    if (hours === 0) hours = 12;

    let timeStr = `${hours}`;
    if (minutes !== 0) {
      const minStr = String(minutes).padStart(2, "0");
      timeStr += `:${minStr}`;
    }
    timeStr += ampm;

    return `${month}/${day}/${year} ${timeStr}`;
  }

  const getGreetingPrefix = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  // 🚫 Content filter with pet-aware context
  function isBlockedMessage(raw, mode) {
    if (!raw) return false;
    const text = raw.toLowerCase();

    const petContextKeywords = [
      "dog",
      "cat",
      "puppy",
      "pup",
      "kitten",
      "pet",
      "my dog",
      "my cat",
      "my puppy",
      "my kitten",
      "neuter",
      "spay",
      "spayed",
      "neutered",
      "fixed",
      "in heat",
      "heat cycle",
      "breeding",
      "breed",
      "mating",
      "litter",
      "stud",
    ];

    const sexualKeywords = [
      "sex",
      "sexual",
      "porn",
      "porno",
      "xxx",
      "onlyfans",
      "nsfw",
      "nude",
      "nudes",
      "blowjob",
      "bj",
      "anal",
      "threesome",
      "stripper",
      "camgirl",
    ];

    const selfHarm = ["kill myself", "suicide", "kys"];
    const extremeViolence = ["behead", "decapitate", "torture"];

    const hasSexual = sexualKeywords.some((kw) => text.includes(kw));
    const hasPetContext = petContextKeywords.some((kw) => text.includes(kw));

    if (hasSexual && hasPetContext) {
      return false;
    }

    if (hasSexual && !hasPetContext) {
      return true;
    }

    if (selfHarm.some((kw) => text.includes(kw))) return true;
    if (extremeViolence.some((kw) => text.includes(kw))) return true;

    return false;
  }


  // ---- helper: fetch existing pet profile from Dynamo via Lambda ----
  const fetchPetProfile = async (tagId) => {
    try {
      const TAG = (tagId || "").toUpperCase();

      const resp = await fetch(SAVE_PET_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "get", tagId: TAG }),
      });

      if (!resp.ok) return null;
      const data = await resp.json();

      if (data && data.petName) {
        return {
          ...data,
          tagId: (data.tagId || TAG).toUpperCase(),
        };
      }

      return null;
    } catch (e) {
      console.error("fetchPetProfile error:", e);
      return null;
    }
  };

  // ---- helper: save pet profile via Lambda ----
  const savePetToDynamo = async (payload) => {
    const enriched = { ...payload };
    enriched.tagId = (enriched.tagId || "").toUpperCase();

    try {
      let headers = { "Content-Type": "application/json" };

      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = user?.signInUserSession;
        const idToken =
          session?.idToken?.jwtToken ||
          session?.idToken?.getJwtToken?.();

        if (idToken) {
          headers = { ...headers, Authorization: idToken };
        }
      } catch {
        // unauthenticated – allowed
      }

      const resp = await fetch(SAVE_PET_LAMBDA_URL, {
        method: "POST",
        headers,
        body: JSON.stringify(enriched),
      });

      if (!resp.ok) throw new Error(`Save failed: ${resp.status}`);
      return true;
    } catch (err) {
      console.error("Save pet error:", err);
      return false;
    }
  };

  // 🖼 Helper: load latest pet photo from S3
  const loadPetPhoto = async (tagId) => {
    if (!tagId) return;
    const TAG = tagId.toUpperCase();

    try {
      setPhotoLoading(true);
      setPhotoError("");
      setPetPhotoUrl(null);

      const listResult = await Storage.list(`pets/${TAG}/`, {
        level: "public",
      });

      const items = listResult?.results || listResult || [];
      if (!items.length) {
        setPhotoLoading(false);
        return;
      }

      const sorted = [...items].sort((a, b) => {
        const aTime = new Date(a.lastModified || 0).getTime();
        const bTime = new Date(b.lastModified || 0).getTime();
        return bTime - aTime;
      });

      const latest = sorted[0];
      const url = await Storage.get(latest.key, { level: "public" });
      setPetPhotoUrl(url);
    } catch (err) {
      console.error("loadPetPhoto error:", err);
      setPhotoError("We couldn’t load your pet photo yet.");
    } finally {
      setPhotoLoading(false);
    }
  };

  // ---------- 1) Enforce tag ownership in OWNER mode ----------
  useEffect(() => {
    let cancelled = false;

    if (finderMode) {
      setOwnershipChecked(true);
      return;
    }

    const checkOwnership = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const rawId =
          user?.username || user?.attributes?.preferred_username || "";
        const tagFromUser = (rawId || "").toUpperCase();
        if (cancelled) return;

        setUserTagId(tagFromUser);

        const friendlyName =
          user?.attributes?.given_name ||
          user?.attributes?.name ||
          user?.username ||
          "";
        setUserName(friendlyName);

        if (!resolvedTagId || resolvedTagId !== tagFromUser) {
              console.warn(
                "Attempted access to different tag:",
                resolvedTagId,
                "for user tag:",
                tagFromUser
              );
              // Go to the protected owner SmartTag route (no :tagId here)
              navigate("/smarttag", { replace: true });
              return;
            }

      } catch (err) {
        console.error("Auth error in SmartTagSetup ownership check:", err);
      } finally {
        if (!cancelled) setOwnershipChecked(true);
      }
    };

    checkOwnership();

    return () => {
      cancelled = true;
    };
  }, [isFinderRoute, resolvedTagId, navigate, finderMode]);

  // ---------- 2) Bootstrap: subscription + initial conversation ----------
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!isFinderRoute && !ownershipChecked) return;

      // OWNER – load pet photo (requires creds)
      if (!isFinderRoute && resolvedTagId) {
        loadPetPhoto(resolvedTagId);
      }

      // 1️⃣ OWNER MODE: check subscription
      if (!isFinderRoute) {
        try {
          const statusRes = await getSubscriptionStatus();
          if (cancelled) return;

          const active = !!statusRes && statusRes.status === "active";
          setHasSubscription(active);
          setSubChecked(true);

          if (!active) {
            setMode("owner");
            setConversation((prev) =>
              prev.length
                ? prev
                : [
                    {
                      sender: "ai",
                      text:
                        "To use SmartTag AI, you’ll need an active plan first. 💳",
                    },
                  ]
            );
            window.dispatchEvent(new CustomEvent("lpp:showPaywall"));
            return;
          }
        } catch (err) {
          console.error("Subscription check failed:", err);
          setSubChecked(true);
          setMode("owner");
          setConversation((prev) =>
            prev.length
              ? prev
              : [
                  {
                    sender: "ai",
                    text:
                      "I couldn’t check your plan right now. Some features may be limited.",
                  },
                ]
          );
        }
      }

      // 2️⃣ FINDER MODE
      // 2️⃣ FINDER MODE
if (queryTag || finderMode) {
  setMode("finder");

  const tagForDisplay =
    resolvedTagId || queryTag || tagParam || "this tag";

  // 🖼 Load pet photo for finder flows too
  const tagForPhoto = (resolvedTagId || queryTag || tagParam || "").toUpperCase();
  if (tagForPhoto) {
    loadPetPhoto(tagForPhoto);
  }

  setConversation((prev) =>
    prev.length
      ? prev
      : [
          {
            sender: "ai",
            text: `Hi there! 🐾 You found a pet wearing tag ${tagForDisplay}.`,
          },
          {
            sender: "ai",
            text:
              "I can help you contact the owner **without sharing phone numbers**.\n" +
              'Just type anything like “yes” or “contact owner” and I’ll open a quick form for you.',
          },
        ]
  );
  return;
}


      // 3️⃣ OWNER MODE – greeting + onboarding / existing pet
      setMode("owner");

      const tagForDisplay = resolvedTagId || rawResolved || pet.tagId || "";
      const greetingPrefix = getGreetingPrefix();
      const friendlyName = userName || "there";

      let existingPet = null;
      if (tagForDisplay) {
        existingPet = await fetchPetProfile(tagForDisplay);
        if (cancelled) return;
      }

      if (existingPet) {
        const mapped = {
          tagId: (existingPet.tagId || tagForDisplay).toUpperCase(),
          name: existingPet.petName || "",
          species: existingPet.species || "",
          breed: existingPet.breed || "",
          age: existingPet.age || "",
        };

        setPet(mapped);
        setCurrentStep(null);

        setConversation((prev) =>
          prev.length
            ? prev
            : [
                {
                  sender: "ai",
                  text:
                    `${greetingPrefix}, ${(friendlyName || "").toUpperCase()}! 🐾\n\n` +
                    `You’re viewing SmartTag **${mapped.tagId}** for **${mapped.name}**.\n` +
                    "I can help you update their profile, set reminders (like vet visits or home meds), and log health visits." +
                    " What would you like to do first?"
                },

              ]
        );
      } else {
        if (status === "active" && atLimit) {
          setCurrentStep(null);

          setConversation((prev) =>
            prev.length
              ? prev
              : [
                  {
                    sender: "ai",
                    text:
                      `Heads up — your plan${
                        planName ? ` **${planName}**` : ""
                      } allows up to **${maxTags} SmartTags**, and you already have **${usedTags}** in use.\n\n` +
                      "To activate this SmartTag, please upgrade your plan or remove a pet from another SmartTag.",
                  },
                  {
                    sender: "ai",
                    type: "cta",
                    buttonLabel: "Manage plan",
                    route: "/subscribe",
                  },
                ]
          );

          return;
        }

        setPet((p) => ({
          ...p,
          tagId: tagForDisplay.toUpperCase(),
        }));
        setCurrentStep("name");

        setConversation((prev) =>
          prev.length
            ? prev
            : [
                {
                  sender: "ai",
                  text:
                    `${greetingPrefix}, ${friendlyName}! 🐾\n\n` +
                    `Let’s set up SmartTag **${tagForDisplay.toUpperCase()}** so we can help if your pet ever gets lost.`,
                },
                {
                  sender: "ai",
                  text: "First, what’s your pet’s **name**?",
                },
              ]
        );
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [
    isFinderRoute,
    resolvedTagId,
    ownershipChecked,
    userName,
    finderMode,
    queryTag,
    rawResolved,
    atLimit,
    maxTags,
    planName,
    pet.tagId,
    usedTags,
    status,
  ]);
















  // ---- helpers: Pet health & reminders via Lambda ----
  const addHealthRecord = async ({
    tagId,
    petName,
    date,
    reason,
    clinic,
    notes,
  }) => {
    try {
      const resp = await fetch(PET_HEALTH_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "addRecord",
          tagId,
          petName,
          date,
          reason,
          clinic,
          notes,
        }),
      });
      return resp.ok;
    } catch (err) {
      console.error("addHealthRecord error:", err);
      return false;
    }
  };
const speakLastAiNow = async (force = false) => {
  const list = convoRef.current || [];
  if (!list.length) return;

  const lastAiIndex = [...list].map((m) => m.sender).lastIndexOf("ai");
  if (lastAiIndex === -1) return;

  // allow re-saying current last AI message when turning on
  lastSpokenAiIndexRef.current = lastAiIndex;

  const b64 = await fetchPollyForText(list[lastAiIndex].text, force);
  if (b64) playPollyBase64(b64, 1);
};

  const addChatNote = async ({ tagId, content }) => {
    if (!tagId || !content) return false;

    try {
      const resp = await fetch(PET_NOTES_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "addNote",
          tagId: tagId.toUpperCase(),
          content,
          category: "Daily",
          mood: "Normal",
        }),
      });

      return resp.ok;
    } catch (err) {
      console.error("addChatNote error:", err);
      return false;
    }
  };

  const listHealthRecords = async (tagId) => {
    try {
      const resp = await fetch(PET_HEALTH_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "listRecords",
          tagId: tagId.toUpperCase(),
        }),
      });
      if (!resp.ok) return [];
      const data = await resp.json();
      return data.items || [];
    } catch (err) {
      console.error("listHealthRecords error:", err);
      return [];
    }
  };

  const addVetReminder = async ({
    tagId,
    petName,
    dateTime,
    reason,
    clinic,
    notes,
  }) => {
    try {
      const resp = await fetch(PET_HEALTH_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "addReminder",
          tagId,
          petName,
          dateTime,
          reason,
          clinic,
          notes,
        }),
      });
      return resp.ok;
    } catch (err) {
      console.error("addVetReminder error:", err);
      return false;
    }
  };

  const listVetReminders = async (tagId) => {
    try {
      const resp = await fetch(PET_HEALTH_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "listReminders",
          tagId: tagId.toUpperCase(),
        }),
      });
      if (!resp.ok) return [];
      const data = await resp.json();
      return data.items || [];
    } catch (err) {
      console.error("listVetReminders error:", err);
      return [];
    }
  };

  // ---------- Pet onboarding + STRICT profile / health flows ----------
  const advanceOnboarding = async (msg) => {
    const text = msg.trim();

    // STRICT UPDATE MODE
    if (profileUpdateMode) {
      const lower = text.toLowerCase();

      if (currentStep === "update-which") {
        let field = null;

        if (lower.includes("name")) field = "name";
        else if (lower.includes("species")) field = "species";
        else if (lower.includes("breed")) field = "breed";
        else if (lower.includes("age") || lower.includes("old")) field = "age";
        else {
          addMsg(
            "ai",
            "I can update **name, species, breed, or age**. Which one would you like to change?"
          );
          return;
        }

        setUpdateField(field);

        if (STRICT_MODE) {
          addMsg(
            "ai",
            "Strict mode for profile updates is ON.\n\n" +
              "To avoid mistakes, I’ll only use exactly what you type — no guessing or auto-fixing.\n" +
              "Please reply with short, clear answers."
          );
        }

        switch (field) {
          case "name":
            addMsg("ai", "What should your pet’s **new name** be?");
            setCurrentStep("update-name");
            break;
          case "species":
            addMsg(
              "ai",
              `What is the correct **species** for ${
                pet.name || "your pet"
              }? (e.g., Dog, Cat)`
            );
            setCurrentStep("update-species");
            break;
          case "breed":
            addMsg(
              "ai",
              `What is the correct **breed** for ${pet.name || "your pet"}?`
            );
            setCurrentStep("update-breed");
            break;
          case "age":
            addMsg(
              "ai",
              `What is the correct **age** for ${
                pet.name || "your pet"
              }? (e.g., 2 years, 8 months)`
            );
            setCurrentStep("update-age");
            break;
          default:
            break;
        }
        return;
      }

      const newValue = text.trim();

      if (currentStep === "update-name") {
        const updated = { ...pet, name: newValue };
        setPet(updated);

        await savePetToDynamo({
          tagId: resolvedTagId,
          petName: updated.name,
          species: updated.species,
          breed: updated.breed,
          age: updated.age,
        });

        addMsg("ai", `All set! I’ve updated the name to **${newValue}**. 🐾`);
      } else if (currentStep === "update-species") {
        const updated = { ...pet, species: newValue };
        setPet(updated);

        await savePetToDynamo({
          tagId: resolvedTagId,
          petName: updated.name,
          species: updated.species,
          breed: updated.breed,
          age: updated.age,
        });

        addMsg(
          "ai",
          `Updated! ${updated.name || "Your pet"} is now a **${newValue}**.`
        );
      } else if (currentStep === "update-breed") {
        const updated = { ...pet, breed: newValue };
        setPet(updated);

        await savePetToDynamo({
          tagId: resolvedTagId,
          petName: updated.name,
          species: updated.species,
          breed: updated.breed,
          age: updated.age,
        });

        addMsg("ai", `Thanks! I updated the breed to **${newValue}**.`);
      } else if (currentStep === "update-age") {
        const normalizedAge = normalizeAgeInput(newValue);
        const updated = { ...pet, age: normalizedAge };
        setPet(updated);

        await savePetToDynamo({
          tagId: resolvedTagId,
          petName: updated.name,
          species: updated.species,
          breed: updated.breed,
          age: updated.age,
        });

        addMsg("ai", `Got it — I updated the age to **${normalizedAge}**.`);
      }

      setProfileUpdateMode(false);
      setUpdateField(null);
      setCurrentStep(null);
      return;
    }

    // ONBOARDING MODE (new SmartTag)
    if (currentStep === "name") {
      const raw = text.trim();

      if (!raw || /\d/.test(raw) || raw.length > 30) {
        addMsg(
          "ai",
          "For your pet’s name, please reply with just their name — for example: **Dale**, **Bella**, or **Max**."
        );
        return;
      }

      const words = raw.split(/\s+/);
      if (words.length > 2) {
        addMsg(
          "ai",
          "That looks a bit long. Please reply with just your pet’s name (1–2 words), like **Dale** or **Coco Bean**."
        );
        return;
      }

      const newName = raw;
      setPet((p) => ({ ...p, name: newName }));

      addMsg(
        "ai",
        `So cute! 🐾 What **species** is ${newName}? (Please reply with a simple word like **Dog** or **Cat**.)`
      );
      setCurrentStep("species");
      return;
    }

    if (currentStep === "species") {
      const raw = text.trim();

      if (!raw || raw.length > 20 || /[^a-zA-Z\s]/.test(raw)) {
        addMsg(
          "ai",
          "For the species, please reply with a simple word like **Dog**, **Cat**, **Rabbit**, or **Bird**."
        );
        return;
      }

      const clean =
        raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();

      setPet((p) => ({ ...p, species: clean }));
      addMsg(
        "ai",
        `Nice! What **breed** is your ${clean}? (You can reply with something like “American Bully” or “Mixed”.)`
      );
      setCurrentStep("breed");
      return;
    }

    if (currentStep === "breed") {
      const raw = text.trim();

      if (!raw || raw.length > 60) {
        addMsg(
          "ai",
          "For the breed, please reply with a short description like “Bully”, “American Bully”, or “Mixed”."
        );
        return;
      }

      setPet((p) => ({ ...p, breed: raw }));
      addMsg(
        "ai",
        `Got it. How **old** is ${pet.name || "your pet"}? (For example: “2 years” or “8 months”.)`
      );
      setCurrentStep("age");
      return;
    }

    if (currentStep === "age") {
      const raw = text.trim();

      if (!/\d/.test(raw) || raw.length > 30) {
        addMsg(
          "ai",
          "For age, please include a number like **“2 years”** or **“8 months”**."
        );
        return;
      }

      const normalized = normalizeAgeInput(raw);

      const finalPet = {
        ...pet,
        age: normalized,
        tagId: resolvedTagId || pet.tagId,
      };
      setPet(finalPet);

      addMsg("ai", "Saving your pet profile… one sec 🐶");

      const ok = await savePetToDynamo({
        tagId: finalPet.tagId.toUpperCase(),
        petName: finalPet.name,
        species: finalPet.species,
        breed: finalPet.breed,
        age: finalPet.age,
      });

      if (ok) {
        addMsg(
          "ai",
          `All set! 🎉 ${finalPet.name} is now saved to your SmartTag.`
        );
        addMsg(
          "ai",
          "You can add photos and more details anytime from your profile."
        );
        addMsg("ai", "", {
          type: "cta",
          action: "viewProfile",
        });
      } else {
        addMsg(
          "ai",
          "⚠️ I couldn’t save that just now. Please try again shortly."
        );
      }

      setCurrentStep(null);
      return;
    }
  };

  const advanceHealthFlow = async (msg) => {
    if (!healthFlow) return;
    const text = msg.trim();
    const lower = text.toLowerCase();

    if (lower === "cancel" || lower.startsWith("cancel ")) {
      setHealthFlow(null);
      addMsg(
        "ai",
        "Okay, I canceled that health task. If you need to cancel an existing appointment, open your Health Timeline and tap **Cancel** next to the reminder."
      );
      return;
    }

    const tagId = (resolvedTagId || pet.tagId || "").toUpperCase();
    const baseDraft = healthFlow.draft || {};

    // Vet reminders
    if (healthFlow.type === "addReminder") {
      if (healthFlow.step === "date") {
        const parsed = parseDateInput(text);
        if (!parsed) {
          addMsg(
            "ai",
            "For reminder dates, please use a format like **12/15/2025** or **2025-12-15**."
          );
          return;
        }

        const draft = {
          ...baseDraft,
          date: parsed.canonical,
          dateDisplay: parsed.display,
        };

        setHealthFlow({ type: "addReminder", step: "time", draft });

        addMsg(
          "ai",
          "Got it. What **time** is this reminder for? (e.g., **3 PM** or **3:30 PM**)"
        );
        return;
      }

      if (healthFlow.step === "time") {
        const parsed = parseTimeInput(text);
        if (!parsed) {
          addMsg(
            "ai",
            "For the time, please use something like **3 PM**, **3:30 PM**, or **15:30**."
          );
          return;
        }

        const draft = {
          ...baseDraft,
          time: parsed.canonical,
          timeDisplay: parsed.display,
        };
        setHealthFlow({ type: "addReminder", step: "reason", draft });

        addMsg(
          "ai",
          "Thanks. What is this reminder **for**? (e.g., “vaccines”, “annual checkup”)"
        );
        return;
      }

      if (healthFlow.step === "reason") {
        const draft = { ...baseDraft, reason: text.trim() };
        setHealthFlow({ type: "addReminder", step: "clinic", draft });

        addMsg(
          "ai",
          "Almost done. Where is this reminder happening? You can type a **clinic/vet name** or something like **“at home”**."
        );
        return;
      }

      if (healthFlow.step === "clinic") {
        const finalDraft = {
          ...baseDraft,
          clinic: text.trim(),
        };

        const dateTimeCanonical = `${finalDraft.date} ${finalDraft.time}`.trim();

        addMsg("ai", "Saving your reminder… ⏰");

        const ok = await addVetReminder({
          tagId,
          petName: pet.name || null,
          dateTime: dateTimeCanonical,
          reason: finalDraft.reason,
          clinic: finalDraft.clinic,
          notes: finalDraft.notes || "",
        });

       if (ok) {
  addMsg(
    "ai",
    `All set! I’ve created a **reminder** for “${finalDraft.reason}” for ${
      pet.name || "your pet"
    } on ${finalDraft.dateDisplay} at ${finalDraft.timeDisplay}.\n\n` +
      "You’ll see this under **Upcoming reminders** in Health & Reminders."
  );

  addMsg("ai", "", {
    type: "cta",
    action: "viewHealth",
  });
}
 else {
          addMsg(
            "ai",
            "⚠️ I couldn’t save that reminder just now. Please try again shortly."
          );
        }

        setHealthFlow(null);
        return;
      }
    }

    // Past visits / health records
    if (healthFlow.type === "addRecord") {
      if (healthFlow.step === "date") {
        const parsed = parseDateInput(text);
        if (!parsed) {
          addMsg(
            "ai",
            "For visit dates, please use a format like **12/15/2025** or **2025-12-15**."
          );
          return;
        }

        const draft = {
          ...baseDraft,
          date: parsed.canonical,
          dateDisplay: parsed.display,
        };

        setHealthFlow({ type: "addRecord", step: "reason", draft });

        addMsg(
          "ai",
          "Got it. What was this visit **for**? (e.g., “ear infection”, “dental cleaning”)"
        );
        return;
      }

      if (healthFlow.step === "reason") {
        const draft = { ...baseDraft, reason: text };
        setHealthFlow({ type: "addRecord", step: "clinic", draft });

        addMsg("ai", "Thanks. Which **clinic or vet** was this with?");
        return;
      }

      if (healthFlow.step === "clinic") {
        const draft = { ...baseDraft, clinic: text };
        setHealthFlow({ type: "addRecord", step: "notes", draft });

        addMsg(
          "ai",
          "Any **notes** you’d like to remember? (symptoms, medications, follow-up, etc.)"
        );
        return;
      }

      if (healthFlow.step === "notes") {
        const finalDraft = {
          ...baseDraft,
          notes: text,
        };

        addMsg("ai", "Saving this visit in your pet’s health history… 📘");

        const ok = await addHealthRecord({
          tagId,
          petName: pet.name || null,
          date: finalDraft.date,
          reason: finalDraft.reason,
          clinic: finalDraft.clinic,
          notes: finalDraft.notes,
        });

       if (ok) {
  addMsg(
    "ai",
    `Done! I added that visit (“${finalDraft.reason}” on ${finalDraft.dateDisplay}) to ${
      pet.name || "your pet"
    }’s **health records**.\n\n` +
      "This is a **record only** — it’s stored in your pet’s history and does **not** send any reminders."
  );

  addMsg("ai", "", {
    type: "cta",
    action: "viewHealth",
  });
}
 else {
          addMsg(
            "ai",
            "⚠️ I couldn’t save that record just now. Please try again shortly."
          );
        }

        setHealthFlow(null);
        return;
      }
    }
  };
  // ---- fuzzy word matching for intent detection ----
  function levenshtein(a, b) {
    if (!a) return b.length;
    if (!b) return a.length;

    const m = [];
    for (let i = 0; i <= b.length; i++) {
      m[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
      m[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        const cost = a[j - 1] === b[i - 1] ? 0 : 1;
        m[i][j] = Math.min(
          m[i - 1][j] + 1,       // deletion
          m[i][j - 1] + 1,       // insertion
          m[i - 1][j - 1] + cost // substitution
        );
      }
    }

    return m[b.length][a.length];
  }

  function hasApproxWord(text, target, maxDistance = 2) {
    if (!text) return false;
    const words = text
      .split(/[^a-z0-9]+/i)
      .map((w) => w.trim())
      .filter(Boolean);

    const tgt = target.toLowerCase();

    return words.some((w) => {
      const lw = w.toLowerCase();
      if (lw === tgt) return true;
      // Quick length filter so we don't do crazy comparisons
      if (Math.abs(lw.length - tgt.length) > maxDistance) return false;
      return levenshtein(lw, tgt) <= maxDistance;
    });
  }

  // ---------- Chat send ----------
  const handleSend = async () => {
    if (!input.trim()) return;
    const msg = input.trim();
    const lower = msg.toLowerCase();

    // FINDER MODE → redirect to contact-support
    if (mode === "finder") {
      const tag = (resolvedTagId || rawResolved || pet.tagId || "").toUpperCase();

      setConversation((prev) => [
        ...prev,
        { sender: "user", text: msg },
        {
          sender: "ai",
          text:
            "Thank you for helping this pet! 💛\n\n" +
            "I’m going to open a quick form where you can share your name and contact info. " +
            "We’ll notify the owner for this SmartTag without exposing anyone’s phone number.",
        },
      ]);
      setInput("");

      const search = new URLSearchParams({
        found: "1",
        tag: tag || "",
      }).toString();

      navigate(`/contact-support?${search}`);
      return;
    }

    // Subscription gate
    if (!queryTag) {
      if (!subChecked) {
        addMsg(
          "ai",
          "One sec — I’m just checking your SmartTag plan before we continue. 🐾"
        );
        return;
      }

      if (!hasSubscription) {
        addMsg("ai", "SmartTag AI is locked until you activate your plan. 💳");
        window.dispatchEvent(new CustomEvent("lpp:showPaywall"));
        return;
      }
    }

    if (
      mode === "owner" &&
      status === "active" &&
      atLimit &&
      !pet.name &&
      !currentStep &&
      !healthFlow
    ) {
      addMsg(
        "ai",
        "Your current SmartTag plan has already reached its limit. To activate this tag, please upgrade your plan or remove a pet from another SmartTag."
      );
      return;
    }

    if (isBlockedMessage(msg, mode)) {
      setConversation((prev) => [
        ...prev,
        { sender: "user", text: msg },
        {
          sender: "ai",
          text:
            "I’m here to help with pets and SmartTags only. 🐾 " +
            "You can ask me about health, behavior, adoption, or everyday care.",
        },
      ]);
      setInput("");
      return;
    }

    setConversation((prev) => [...prev, { sender: "user", text: msg }]);
    setInput("");

    if (currentStep) {
      await advanceOnboarding(msg);
      return;
    }

    if (healthFlow) {
      await advanceHealthFlow(msg);
      return;
    }

    // Quick pet note
    if (mode === "owner" && resolvedTagId) {
      const looksLikeNote =
        lower.startsWith("log ") ||
        lower.startsWith("note ") ||
        lower.includes("log that") ||
        lower.includes("save a note") ||
        lower.includes("add a note") ||
        lower.includes("journal");

      if (looksLikeNote) {
        let content = msg;

        const patterns = [
          /^log (that )?/i,
          /^note (that )?/i,
          /^save a note[:\-]?\s*/i,
          /^add a note[:\-]?\s*/i,
        ];

        for (const p of patterns) {
          if (p.test(content)) {
            content = content.replace(p, "").trim();
            break;
          }
        }

        if (!content) content = msg;

        const ok = await addChatNote({
          tagId: resolvedTagId,
          content,
        });

        if (ok) {
          addMsg("ai", "Got it — I saved that in your pet notes journal. 📝");
        } else {
          addMsg(
            "ai",
            "I tried to save that note but something went wrong. Please try again in a bit."
          );
        }

        return;
      }
    }

    // Strict profile update trigger
    if (mode === "owner" && pet?.name && !profileUpdateMode) {
      const wantsUpdateProfile =
        lower.includes("update profile") ||
        lower.includes("edit profile") ||
        lower.includes("update pet info") ||
        lower.includes("update pet information") ||
        lower.includes("change his name") ||
        lower.includes("change her name") ||
        lower.includes("change the name") ||
        lower.includes("fix the name") ||
        lower.includes("correct her name") ||
        lower.includes("correct his name") ||
        ((lower.includes("update") ||
          lower.includes("edit") ||
          lower.includes("change") ||
          lower.includes("fix") ||
          lower.includes("correct")) &&
          (lower.includes("profile") ||
            lower.includes("pet info") ||
            lower.includes("pet information") ||
            lower.includes("name")));

      if (wantsUpdateProfile) {
        setProfileUpdateMode(true);
        setCurrentStep("update-which");

        addMsg(
          "ai",
          "No problem — I’ll help you update the profile in **strict mode** so everything is stored exactly as you type it.\n\n" +
            "Strict mode for profile updates is ON.\n" +
            "To avoid mistakes, I’ll only use exactly what you type — no guessing or auto-fixing.\n" +
            "Please reply with short, clear answers.\n\n" +
            "Which part would you like to update: **name, species, breed, or age**?"
        );
        return;
      }

      if (lower.includes("photo")) {
        addMsg(
          "ai",
          "Photo uploads from this screen are coming soon 📷 — use your profile for now."
        );
        return;
      }
      if (lower.includes("share") || lower.includes("qr")) {
        addMsg(
          "ai",
          "Shareable contact QR links are coming soon 🔗. For now, you can share your tag ID."
        );
        return;
      }
    }

    // Health intents
        // Health intents
    if (mode === "owner" && resolvedTagId) {
      // More tolerant matching for reminders / appointments
      const reminderish =
        hasApproxWord(lower, "reminder", 2) ||
        lower.includes("remind me") ||
        lower.includes("remind us");

      const appointmentish =
        hasApproxWord(lower, "appointment", 2) ||
        lower.includes("appt");

      const cancelish =
        lower.includes("cancel") ||
        lower.includes("remove") ||
        lower.includes("delete");

      const wantsCancelAppointment =
        cancelish &&
        (appointmentish || reminderish || lower.includes("vet"));

      if (wantsCancelAppointment) {
        addMsg(
          "ai",
          "I can’t cancel existing vet reminders directly from chat yet.\n\n" +
            "To cancel an appointment, open your **Health Timeline** and tap **Cancel** next to the reminder you want to remove."
        );
        return;
      }

      const wantsNewReminder =
        reminderish ||
        lower.includes("set a reminder") ||
        lower.includes("set reminder") ||
        (appointmentish && !lower.includes("past"));

      const wantsNewRecord =
        lower.includes("add record") ||
        lower.includes("medical record") ||
        lower.includes("log visit") ||
        lower.includes("log a visit") ||
        lower.includes("add a record") ||
        lower.includes("past visit");

    if (wantsNewReminder) {
  setHealthFlow({
    type: "addReminder",
    step: "date",
    draft: {},
  });

  addMsg(
    "ai",
    "Okay, we’ll set up a **reminder** for something in the future. ⏰\n\n" +
      "- This will show under **Upcoming reminders** in Health & Reminders.\n" +
      "- It’s meant for appointments, medications, or anything you don’t want to forget.\n\n" +
      "For accuracy and safety, we’ll enter **Strict Mode** and I’ll ask for one detail at a time.\n\n" +
      "First, what is the **date** for this reminder? (e.g., 12/15/2025)"
  );

  return;
}


    if (wantsNewRecord) {
  setHealthFlow({
    type: "addRecord",
    step: "date",
    draft: {},
  });

  addMsg(
    "ai",
    "Got it — we’ll **log a visit in your pet’s health history**. 📘\n\n" +
      "- This creates a **health record** for something that already happened.\n" +
      "- It will show under **Recent health records** and **does not send reminders or alerts.**\n\n" +
      "We’ll use **Strict Mode** and go one detail at a time.\n\n" +
      "First, what is the **date** of this visit? (e.g., 12/15/2025)"
  );
  return;
}


      const hasReminderWord = reminderish || appointmentish;
      const hasRecordWord =
        lower.includes("add record") ||
        lower.includes("log visit") ||
        lower.includes("medical record") ||
        lower.includes("add a record") ||
        lower.includes("log a visit");


      if (hasReminderWord) {
        setHealthFlow({
          type: "addReminder",
          step: "date",
          draft: {},
        });

        addMsg(
          "ai",
          "For accuracy and safety, we’ll enter **Strict Mode** for health tasks.\n" +
            "I’ll record one detail at a time, exactly as you type it.\n\n" +
            "First, what is the **date** of your appointment?"
        );
        return;
      }

      if (hasRecordWord) {
        setHealthFlow({
          type: "addRecord",
          step: "date",
          draft: {},
        });

        addMsg(
          "ai",
          "For accuracy and safety, we’ll enter **Strict Mode** for health history.\n" +
            "I’ll record one detail at a time, exactly as you type it.\n\n" +
            "First, what is the **date** of this visit?"
        );
        return;
      }

      if (lower.includes("history") || lower.includes("records")) {
        addMsg("ai", "Let me pull your pet’s recent health records…");
        const items = await listHealthRecords(resolvedTagId);

        if (!items.length) {
          addMsg(
            "ai",
            'I don’t see any records yet. You can say "add record" to start one.'
          );
        } else {
          const lines = items.slice(0, 5).map((it) => {
            const rawDate =
              it.date ||
              (it.sk && it.sk.startsWith("REC#")
                ? it.sk.replace("REC#", "")
                : null);
            const pretty = rawDate
              ? formatDateTimeDisplay(rawDate)
              : "unknown date";
            return `• ${it.reason} – ${pretty} (${it.clinic || "no clinic saved"})`;
          });
          addMsg("ai", `Here are the latest visits:\n${lines.join("\n")}`);
        }

        addMsg("ai", "View Health Timeline", {
          type: "cta",
          action: "viewHealth",
        });

        return;
      }

      if (lower.includes("upcoming") || lower.includes("next appointment")) {
        addMsg("ai", "Checking your upcoming vet reminders…");
        const reminders = await listVetReminders(resolvedTagId);

        if (!reminders.length) {
          addMsg(
            "ai",
            'I don’t see any upcoming vet reminders yet. You can say "set a vet reminder" to add one.'
          );
        } else {
          const lines = reminders.slice(0, 5).map((it) => {
            const rawWhen =
              it.dateTime ||
              (it.sk && it.sk.startsWith("REM#")
                ? it.sk.replace("REM#", "")
                : null);
            const pretty = rawWhen
              ? formatDateTimeDisplay(rawWhen)
              : "unknown time";

            const location = it.clinic && it.clinic.trim();
            if (location) {
              return `• ${pretty} – ${it.reason} (${location})`;
            }
            return `• ${pretty} – ${it.reason}`;
          });

          addMsg("ai", `Here are your next reminders:\n${lines.join("\n")}`);
        }

        addMsg("ai", "View Health Timeline", {
          type: "cta",
          action: "viewHealth",
        });

        return;
      }
    }

    // Fallback → OpenAI SmartTag mode
    setLoading(true);

    try {
      const resp = await fetch(OPENAI_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tagId: resolvedTagId || undefined,
          userMessage: msg,
          mode: queryTag ? "finder" : "smarttag",
        }),
      });

      const data = await resp.json();
      addMsg("ai", data.reply || "Got it!");
    } catch (err) {
      console.error("Chat error:", err);
      addMsg("ai", "Hmm, something went wrong. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  const subtitle =
    mode === "finder" ? "SmartTag: Finder Mode" : "SmartTag: Owner Mode";

  // Health strict-mode summary (UI info bar)
  let healthSummary = null;
  if (healthFlow) {
    const isReminder = healthFlow.type === "addReminder";
    const draft = healthFlow.draft || {};

    let totalSteps = 4;
    let currentStepNum = 1;
    let stepLabel = "";

    if (isReminder) {
      switch (healthFlow.step) {
        case "date":
          currentStepNum = 1;
          stepLabel = "Date";
          break;
        case "time":
          currentStepNum = 2;
          stepLabel = "Time";
          break;
        case "reason":
          currentStepNum = 3;
          stepLabel = "Reason";
          break;
        case "clinic":
          currentStepNum = 4;
          stepLabel = "Clinic";
          break;
        default:
          stepLabel = "Details";
      }
    } else {
      switch (healthFlow.step) {
        case "date":
          currentStepNum = 1;
          stepLabel = "Date";
          break;
        case "reason":
          currentStepNum = 2;
          stepLabel = "Reason";
          break;
        case "clinic":
          currentStepNum = 3;
          stepLabel = "Clinic";
          break;
        case "notes":
          currentStepNum = 4;
          stepLabel = "Notes";
          break;
        default:
          stepLabel = "Details";
      }
    }

    healthSummary = {
      isReminder,
      draft,
      totalSteps,
      currentStep: currentStepNum,
      stepLabel,
    };
  }

  // Onboarding summary
  let onboardingSummary = null;
  const onboardingSteps = ["name", "species", "breed", "age"];
  if (
    !healthFlow &&
    !profileUpdateMode &&
    currentStep &&
    onboardingSteps.includes(currentStep)
  ) {
    const totalSteps = onboardingSteps.length;
    const currentIndex = onboardingSteps.indexOf(currentStep);
    const currentStepNum = currentIndex === -1 ? 1 : currentIndex + 1;

    let stepLabel = "";
    switch (currentStep) {
      case "name":
        stepLabel = "Name";
        break;
      case "species":
        stepLabel = "Species";
        break;
      case "breed":
        stepLabel = "Breed";
        break;
      case "age":
        stepLabel = "Age";
        break;
      default:
        stepLabel = "Details";
    }

    onboardingSummary = {
      totalSteps,
      currentStep: currentStepNum,
      stepLabel,
    };
  }

  if (!queryTag && !ownershipChecked) {
    return null;
  }

  const isDatePickerStep =
    !!healthFlow &&
    healthFlow.step === "date" &&
    (healthFlow.type === "addReminder" || healthFlow.type === "addRecord");

return (
  <div className="smarttag-wrapper">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="smarttag-shell"
    >
      {/* Header – matches PawChat style but with pet + plan info */}
     <PetPageHeader
  baseTitle="Paw AI Assistant"
  subtitle={
    mode === "finder"
      ? "SmartTag: Finder Mode"
      : "SmartTag: Owner Mode"
  }
  tagLabelPrefix="Tag:"
  overrideTagId={resolvedTagId}   // optional if you already have it
  overridePetName={pet.name}      // optional
  overridePhotoUrl={petPhotoUrl}  // optional
>


  {/* This block replaces your old plan summary area in the header (owner mode only) */}
  {mode === "owner" && status === "active" && (
    <div className="smarttag-plan-summary mt-1">
      {planName && (
        <div>
          Plan: <strong>{planName}</strong>
        </div>
      )}

      {maxTags > 0 ? (
        <div>
          SmartTags in use:{" "}
          <strong>
            {usedTags} of {maxTags}
          </strong>
        </div>
      ) : (
        <div>
          SmartTags in use: <strong>{usedTags}</strong>
        </div>
      )}

      {atLimit && (
        <div className="smarttag-plan-warning">
          You’ve reached your SmartTag limit for this plan.
        </div>
      )}
    </div>
  )}
</PetPageHeader>

      {/* Strict health flow summary bar (brand-matched) */}
      {healthSummary && (
        <div className="smarttag-health-summary">
          <div className="smarttag-health-summary-top">
          <div className="smarttag-health-label">
  Health Strict Mode ·{" "}
  {healthSummary.isReminder
    ? "Scheduling a reminder (future task)"
    : "Logging a visit (record only)"}
</div>

            <div className="smarttag-health-step-pill">
              Step {healthSummary.currentStep} of {healthSummary.totalSteps} ·{" "}
              {healthSummary.stepLabel}
            </div>
          </div>

          <div className="smarttag-health-summary-body">
            <div>
              <strong>Date:</strong>{" "}
              {healthSummary.draft.dateDisplay || healthSummary.draft.date ? (
                <span>
                  {healthSummary.draft.dateDisplay || healthSummary.draft.date}
                </span>
              ) : (
                <span className="text-muted">waiting…</span>
              )}
            </div>

            {healthSummary.isReminder && (
              <div>
                <strong>Time:</strong>{" "}
                {healthSummary.draft.timeDisplay ||
                healthSummary.draft.time ? (
                  <span>
                    {healthSummary.draft.timeDisplay ||
                      healthSummary.draft.time}
                  </span>
                ) : (
                  <span className="text-muted">waiting…</span>
                )}
              </div>
            )}

            <div>
              <strong>Reason:</strong>{" "}
              {healthSummary.draft.reason ? (
                <span>{healthSummary.draft.reason}</span>
              ) : (
                <span className="text-muted">waiting…</span>
              )}
            </div>

            <div>
              <strong>Location / clinic:</strong>{" "}
              {healthSummary.draft.clinic ? (
                <span>{healthSummary.draft.clinic}</span>
              ) : (
                <span className="text-muted">waiting…</span>
              )}
            </div>

            {!healthSummary.isReminder && (
              <div>
                <strong>Notes:</strong>{" "}
                {healthSummary.draft.notes ? (
                  <span>{healthSummary.draft.notes}</span>
                ) : (
                  <span className="text-muted">optional</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 🔽 Scrollable chat box */}
      <div className="chat-box smarttag-chat-box">
        {conversation.map((msg, i) => {
          if (msg.type === "cta") {
            const label =
              msg.buttonLabel ||
              msg.text ||
              (msg.action === "viewProfile"
                ? "View Pet Profile"
                : msg.action === "viewHealth"
                ? "View Health Timeline"
                : "Open");

            const handleClick = () => {
              const tag = (resolvedTagId || pet.tagId || "").toUpperCase();

              if (msg.action === "viewProfile") {
                if (tag) {
                  navigate(`/profile?tag=${encodeURIComponent(tag)}`);
                } else {
                  navigate("/profile");
                }
              } else if (msg.action === "viewHealth") {
                const search = tag ? `?tag=${encodeURIComponent(tag)}` : "";
                navigate(`/health${search}`);
              } else if (msg.route) {
                navigate(msg.route);
              }
            };

            return (
              <div
                key={i}
                className="chat-bubble ai-bubble smarttag-cta-bubble"
              >
                <Button
                  type="button"
                  className="lpp-pill-btn-outline"
                  onClick={handleClick}
                >
                  {label}
                </Button>
              </div>
            );
          }

          return (
            <div
              key={i}
              className={`chat-bubble ${
                msg.sender === "ai" ? "ai-bubble" : "user-bubble"
              }`}
            >
              {msg.text}
            </div>
          );
        })}

        {loading && (
          <div className="chat-bubble ai-bubble paw-typing-wrapper">
            <span className="paw-typing-label">Paw is thinking</span>
            <div className="paw-typing-indicator">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        )}




        {/* 👇 Invisible anchor for auto-scroll – ALWAYS last */}
        <div style={{ height: "4.75rem" }} />
        <div ref={chatEndRef} />

      </div>

    <div className="smarttag-shell">
  {/* chat messages ... */}

 <Form
  className="chat-input-row"
  onSubmit={(e) => {
    e.preventDefault();
    handleSend();
  }}
>
  <div className="lpp-input-bar-container">
    <div className="chat-input-pill">
      <div className="chat-input-inner">
        <Button
          size="sm"
          className={`lpp-voice-btn ${voiceEnabled ? "is-on" : "is-off"}`}
          onClick={async () => {
            const next = !voiceEnabled;
            setVoiceEnabled(next);

            if (!next) {
              stopVoice();
              voicePrimedRef.current = false;
              lastSpokenAiIndexRef.current = -1;
              return;
            }

            voicePrimedRef.current = false;
            lastSpokenAiIndexRef.current = -1;

            await primeVoice(true);
            await speakLastAiNow(true);
          }}
          title="Toggle voice"
          aria-label={voiceEnabled ? "Voice on" : "Voice off"}
          type="button"
        >
          <FontAwesomeIcon icon={voiceEnabled ? faVolumeHigh : faVolumeXmark} />
        </Button>

        <Form.Control
          type={isDatePickerStep ? "date" : "text"}
          placeholder={
            isDatePickerStep
              ? "Select a date…"
              : currentStep
              ? "Type your answer..."
              : "Type your message..."
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="chat-input-field"
        />

        <button type="submit" disabled={loading} className="chat-send-icon-btn">
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
      </div>
    </div>
  </div>
</Form>


  {/* ✅ anchored, not part of the form layout */}
  <div className="">
    <FloatingPawButton isAuthenticated={true} hasActiveSub={true} />
  </div>
</div>
    </motion.div>
  </div>
);



}
